import { useEffect, useState } from 'react';
import { 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box,
  Alert
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { BaseDialog } from './base';

/**
 * Detect if browser is in incognito/private mode
 */
const detectIncognito = async () => {
  return new Promise((resolve) => {
    // Chrome/Edge detection
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      navigator.storage.estimate().then(({ usage, quota }) => {
        // In incognito, quota is usually very small (< 120MB)
        resolve(quota < 120000000);
      }).catch(() => resolve(false));
    } 
    // Firefox detection
    else if ('MozAppearance' in document.documentElement.style) {
      const db = indexedDB.open('test');
      db.onerror = () => resolve(true);
      db.onsuccess = () => resolve(false);
    }
    // Safari detection
    else if (window.safari !== undefined) {
      try {
        window.openDatabase(null, null, null, null);
        resolve(false);
      } catch (e) {
        resolve(true);
      }
    }
    // Fallback - assume not incognito
    else {
      resolve(false);
    }
  });
};

export function IncognitoDialog() {
  const [isIncognito, setIsIncognito] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    // Check on mount
    detectIncognito().then((incognito) => {
      setIsIncognito(incognito);
      // Only show dialog if incognito AND user hasn't dismissed it this session
      if (incognito && !sessionStorage.getItem('incognito_warning_dismissed')) {
        setShowDialog(true);
      }
    });
  }, []);

  const handleContinue = () => {
    // Remember user dismissed the warning for this session
    sessionStorage.setItem('incognito_warning_dismissed', 'true');
    setShowDialog(false);
  };

  const handleSwitchWindow = () => {
    // Show instructions on how to open in normal window
    alert('Please:\n1. Copy this URL\n2. Open a new regular browser window\n3. Paste the URL and continue');
  };

  if (!isIncognito || !showDialog) return null;

  return (
    <BaseDialog
      open={showDialog}
      title="Private/Incognito Mode Detected"
      onClose={handleContinue}
    >
      <DialogContent>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 2, 
          pt: 1,
          minWidth: 320 
        }}>
          <Visibility sx={{ fontSize: 48, color: 'warning.main' }} />
          
          <Alert severity="warning" sx={{ width: '100%' }}>
            <Typography variant="body2">
              You're browsing in <strong>Private/Incognito mode</strong>
            </Typography>
          </Alert>

          <Typography variant="body1" textAlign="center">
            Authentication may not work properly in private mode due to browser cookie restrictions.
          </Typography>

          <Typography variant="body2" color="text.secondary" textAlign="center">
            For the best experience, we recommend using a regular browser window.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ pb: 2, px: 3, flexDirection: 'column', gap: 1 }}>
        <Button 
          onClick={handleSwitchWindow} 
          variant="contained"
          fullWidth
          color="primary"
        >
          How to Switch Windows
        </Button>
        <Button 
          variant="outlined" 
          onClick={handleContinue}
          fullWidth
        >
          Continue Anyway
        </Button>
      </DialogActions>
    </BaseDialog>
  );
}